/**
 * cockpit-v2 Button — five archetypes, one component, dual-mode.
 *
 * Usage:
 *   <Button archetype="primary">Submit Run</Button>
 *   <Button archetype="destructive" size="sm">Delete</Button>
 *   <Button archetype="capture" engaged={isCurrent}>Aspirin 324 mg</Button>
 *   <Button archetype="quiet">Skip</Button>
 *
 * Spec: docs/strategy/EMSPCR_UI_BUILD_SPEC_V1.md §2.1
 *
 * Dual-mode: subscribes to useThemeStore().cockpitV2 and renders the
 * v2 cockpit button when the flag is on, falls back to the v1 .btn-*
 * class when off. Call sites import Button and don't think about it —
 * flipping the feature flag swaps every Button across the product.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { useThemeStore } from '@/lib/theme-store';

export type ButtonArchetype =
  | 'primary'
  | 'destructive'
  | 'secondary'
  | 'capture'
  | 'quiet';

export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  archetype: ButtonArchetype;
  /** Defaults to 'md' (48px touch floor). 'sm' permitted in non-field
   *  contexts only (admin, modals). */
  size?: ButtonSize;
  /** For capture archetype: when true, the bezel-light pip lights up
   *  and the button stays slightly depressed. Reflects to aria-pressed. */
  engaged?: boolean;
  /** Optional leading icon (lucide-react usually). */
  icon?: ReactNode;
  children: ReactNode;
}

/**
 * Map a v2 archetype to the closest v1 class for legacy fallback.
 * The legacy console theme has a smaller archetype set (primary,
 * secondary, danger, rail-action), so the mapping is approximate.
 */
const LEGACY_CLASS: Record<ButtonArchetype, string> = {
  primary:     'btn-primary',
  destructive: 'btn-danger',
  secondary:   'btn-secondary',
  capture:     'rail-action',                    // closest v1 equivalent
  quiet:       'text-xs font-mono uppercase tracking-widest text-slate-400 hover:text-slate-200',
};

export function Button({
  archetype,
  size = 'md',
  engaged,
  icon,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const cockpitV2 = useThemeStore((s) => s.cockpitV2);

  if (!cockpitV2) {
    // Legacy fallback — render with v1 classes. Existing call sites
    // continue to look identical to today when v2 is off.
    return (
      <button
        type={type}
        className={cn(LEGACY_CLASS[archetype], className)}
        aria-pressed={archetype === 'capture' ? Boolean(engaged) : undefined}
        {...rest}
      >
        {icon}
        {children}
      </button>
    );
  }

  // cockpit-v2 rendering
  const archetypeClass = `btn-v2-${archetype}`;
  const sizeClass = `btn-v2-size-${size}`;
  return (
    <button
      type={type}
      className={cn('btn-v2', archetypeClass, sizeClass, className)}
      data-engaged={archetype === 'capture' && engaged ? 'true' : undefined}
      aria-pressed={archetype === 'capture' ? Boolean(engaged) : undefined}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}
